import { useApi } from '../lib/hooks';
import { BackLink, ErrorState, Header, Loader, Screen, TabBar } from '../components/Chrome';

/** A6 · Правила начисления (SPEC §5, §6). Числа приходят из движка очков. */

interface RulesResponse {
  points: {
    workout: number;
    maxWorkoutsPerDay: number;
    meal: number;
    maxMealsPerDay: number;
    streakStep: number;
    streakMax: number;
    dayMax: number;
    minMinutes: number;
    minSets: number;
  };
  streakExamples: { days: number; bonus: number }[];
  leagues: { tier: number; name: string; icon: string }[];
}

export function RulesScreen() {
  const { data, error, loading, reload } = useApi<RulesResponse>('/api/arena/rules');

  return (
    <Screen tabs>
      <Header />
      <BackLink to="/arena" label="Арена" />
      <h1>Правила начисления</h1>
      <p className="nt" style={{ margin: '6px 0 16px', fontSize: 14 }}>
        Очки дают за регулярность, а не за веса и калории. Лиги считаются по неделям, рейтинг
        залов — по календарному месяцу.
      </p>

      {loading ? <Loader /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {data ? (
        <>
          <section className="card">
            <div className="rule">
              <span className="ico emo" aria-hidden="true">
                🏋️
              </span>
              <div>
                <b>Тренировка</b>
                <small>
                  За каждую в течение дня, но засчитываются не больше{' '}
                  {data.points.maxWorkoutsPerDay} в день. Нужно от {data.points.minMinutes} минут
                  или от {data.points.minSets} подходов
                </small>
              </div>
              <span className="big">
                +{data.points.workout}
                <img className="pt-ic" src="/assets/ui/points-diamond-bicep.webp" alt="" />
              </span>
            </div>

            <div className="rule">
              <span className="ico emo" aria-hidden="true">
                🍽️
              </span>
              <div>
                <b>Приём пищи</b>
                <small>
                  Завтрак, обед, ужин и перекусы — каждый по +{data.points.meal}, в приёме хотя
                  бы один продукт. До +{data.points.meal * data.points.maxMealsPerDay} в день
                </small>
              </div>
              <span className="big">
                +{data.points.meal}
                <img className="pt-ic" src="/assets/ui/points-diamond-bicep.webp" alt="" />
              </span>
            </div>

            <div className="rule">
              <span className="ico emo" aria-hidden="true">
                🔥
              </span>
              <div>
                <b>Серия питания</b>
                <small>
                  Со 2-го дня подряд с записями еды: +{data.points.streakStep} × длина серии,
                  максимум +{data.points.streakMax} в день
                </small>
              </div>
              <span className="big">
                +{data.points.streakStep}×
                <img className="pt-ic" src="/assets/ui/points-diamond-bicep.webp" alt="" />
              </span>
            </div>

            <div className="steps" aria-label="Бонус серии по дням">
              {data.streakExamples.map((example, index) => (
                <div
                  key={example.days}
                  className={index === data.streakExamples.length - 1 ? 'hi' : undefined}
                >
                  {example.days === 1
                    ? '1 день'
                    : index === data.streakExamples.length - 1
                      ? `${example.days}+`
                      : `${example.days} дней`}
                  <b>{example.bonus === 0 ? '0' : `+${example.bonus}`}</b>
                </div>
              ))}
            </div>
          </section>

          <section className="card">
            <h2>Лиги</h2>
            <p className="nt" style={{ margin: '6px 0 12px', fontSize: 14 }}>
              10 лиг — от Железа до Алмаза. Каждую неделю вы соревнуетесь с группой до 30 человек
              своей лиги.
            </p>
            <div className="lgg">
              {data.leagues.map((league) => (
                <div key={league.tier}>
                  <img src={league.icon} alt="" />
                  <span>{league.name}</span>
                </div>
              ))}
            </div>
            <ul className="pl">
              <li>
                Неделя — с понедельника 00:00 до воскресенья 23:59. В лиге считаются очки только
                этой недели.
              </li>
              <li>
                Группа собирается, когда вы получили первые очки на неделе, — поэтому в ней только
                активные участники.
              </li>
              <li>
                <b>Топ-7</b> переходят в лигу выше, <b>последние 5</b> — опускаются, остальные
                остаются.
              </li>
              <li>Новички начинают в Железе. Из Железа не вылетают.</li>
              <li>
                В Алмазе подниматься некуда: топ-3 недели получают значок, последние 5 опускаются
                в Аметист.
              </li>
              <li>
                Тренировка, внесённая задним числом больше чем за сутки, в зачёт лиги не идёт.
              </li>
              <li>
                <b>Залы</b> тоже соревнуются в лигах, но по месяцу: группа до 12 залов, топ-3
                поднимаются, последние 3 опускаются. Очки зала — сумма очков участников.
              </li>
            </ul>
          </section>

          <section className="card">
            <h2>Пример дня</h2>
            <div className="exlist">
              <div>
                <span>Тренировка в зале</span>
                <span>+{data.points.workout}</span>
              </div>
              <div>
                <span>Завтрак, обед, ужин, перекус</span>
                <span>+{data.points.meal * data.points.maxMealsPerDay}</span>
              </div>
              <div>
                <span>5-й день серии питания</span>
                <span>+{data.points.streakStep * 5}</span>
              </div>
              <div className="total">
                <span>Итого за день</span>
                <span className="pts">
                  +
                  {data.points.workout +
                    data.points.meal * data.points.maxMealsPerDay +
                    data.points.streakStep * 5}
                  <img className="pt-ic" src="/assets/ui/points-diamond-bicep.webp" alt="" />
                </span>
              </div>
            </div>
            <p className="nt" style={{ margin: '12px 0 0' }}>
              Максимум за день — {data.points.dayMax} очков.
            </p>
          </section>

          <section className="card">
            <h2>Как считается</h2>
            <ul className="pl">
              <li>День — по вашему местному времени, с 00:00 до 23:59.</li>
              <li>
                Очки зала — сумма очков его участников. Тренировка идёт в зачёт залу, выбранному
                в ней.
              </li>
              <li>Правка или удаление записи сразу пересчитывает очки. Черновики не считаются.</li>
              <li>Пропуск дня обнуляет серию питания, очки за прошлые дни остаются.</li>
            </ul>
          </section>
        </>
      ) : null}

      <TabBar />
    </Screen>
  );
}
